/** Text helpers the back-testing and dataset screens share. */

import { getApiErrorMessage } from '@/lib/utils';

/** SOP intents are slugs (`cancel-order`); render them as words, capitalized by CSS. */
export const sopTitle = (intent: string): string => intent.replace(/[-_]/g, ' ');

/** A SOP's title where it stands alone, capitalized rather than left to a CSS class. */
export const sopHeading = (intent: string): string => {
	const title = sopTitle(intent);
	return title.charAt(0).toUpperCase() + title.slice(1);
};

export const seconds = (ms: number | null): string => (ms === null ? '' : `${Math.round(ms / 1000)}s`);

/** A count with its noun, plural unless there is one: "1 past ticket", "36 scenario types". */
export const counted = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * A failure in words: the server's message, else a plain Error's own, which is how a stream's
 * error event and a request that never reached the server arrive.
 */
export const failureMessage = (err: unknown, fallback: string): string => {
	if (err && typeof err === 'object' && 'response' in err) return getApiErrorMessage(err, fallback);
	return err instanceof Error && err.message ? err.message : fallback;
};

/**
 * Who a scenario is about: the scenario world's own customer fields win,
 * the ticket's customer (server-attached) fills in when the world has none.
 */
export const scenarioContact = (scenario: {
	toolOutput?: unknown;
	customer?: { name: string | null; email: string | null } | null;
}): { name: string | null; email: string | null } => {
	const world = (scenario.toolOutput ?? {}) as Record<string, unknown>;
	const pick = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);
	return {
		name: pick(world['customer_name']) ?? pick(scenario.customer?.name),
		email: pick(world['customer_email']) ?? pick(scenario.customer?.email)
	};
};

/** Name and email on one line, for a scenario's full contact. */
export const scenarioCustomer = (scenario: Parameters<typeof scenarioContact>[0]): string | null => {
	const { name, email } = scenarioContact(scenario);
	if (name && email) return `${name} · ${email}`;
	return name ?? email;
};

/**
 * Who fabricated a scenario and when, as its detail says it: "Fabricated by you on Oct 8, 2026". The
 * signed-in user's id arrives as a number at runtime whatever its type says, so both sides compare as text.
 */
export const fabricatedBy = (
	createdBy: number | null,
	currentUserId: string | number | null,
	createdAt?: string
): string => {
	const byYou = createdBy !== null && currentUserId !== null && String(createdBy) === String(currentUserId);
	const date = createdAt ? new Date(createdAt) : null;
	const on =
		date && !Number.isNaN(date.getTime())
			? ` on ${date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`
			: '';
	return `Fabricated${byYou ? ' by you' : ''}${on}`;
};

/** The one name a scenario row leads with. */
export const scenarioWho = (scenario: Parameters<typeof scenarioContact>[0]): string | null => {
	const { name, email } = scenarioContact(scenario);
	return name ?? email;
};

/**
 * Threads scraped before the server stripped markup still carry literal `<br>`.
 * A row preview is one line of prose, so they read as the spaces they separated.
 */
export const previewText = (text: string): string =>
	text
		.replace(/<br\s*\/?>/gi, ' ')
		.replace(/\s+/g, ' ')
		.trim();

/**
 * The same text in a thread bubble, which wraps on its own newlines: there the
 * `<br>` was a line break and should read as one. Runs of blank lines collapse,
 * since a scraped signature block is mostly those.
 */
export const threadText = (text: string): string =>
	text
		.replace(/<br\s*\/?>/gi, '\n')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
